import {
    AppwriteException,
    Client,
    type Payload,
    applyDefaults,
} from '../client';
import type { Models } from '../models';

export class Presences {
    client: Client;

    constructor(client: Client) {
        this.client = client;
    }

    /**
     * List presence logs. Expired entries are filtered out automatically.
     *
     *
     * @param {string[]} params.queries - Array of query strings generated using the Query class provided by the SDK.
     * @param {boolean} params.total - When set to false, the total count returned will be 0 and will not be calculated.
     * @param {number} params.ttl - TTL (seconds) for caching list responses. Responses are stored in an in-memory key-value cache, keyed per project, collection, schema version (attributes and indexes), caller authorization roles, and the exact query — so users with different permissions never share cached entries. Schema changes invalidate cached entries automatically; document writes do not, so choose a TTL you are comfortable serving as stale data. Set to 0 to disable caching. Must be between 0 and 86400 (24 hours).
     * @throws {AppwriteException}
     * @returns {Promise<Models.PresenceList>}
     */
    list(params?: {
        queries?: string[];
        total?: boolean;
        ttl?: number;
    }): Promise<Models.PresenceList>;
    /**
     * List presence logs. Expired entries are filtered out automatically.
     *
     *
     * @param {string[]} queries - Array of query strings generated using the Query class provided by the SDK.
     * @param {boolean} total - When set to false, the total count returned will be 0 and will not be calculated.
     * @param {number} ttl - TTL (seconds) for caching list responses. Responses are stored in an in-memory key-value cache, keyed per project, collection, schema version (attributes and indexes), caller authorization roles, and the exact query — so users with different permissions never share cached entries. Schema changes invalidate cached entries automatically; document writes do not, so choose a TTL you are comfortable serving as stale data. Set to 0 to disable caching. Must be between 0 and 86400 (24 hours).
     * @throws {AppwriteException}
     * @returns {Promise<Models.PresenceList>}
     * @deprecated Use the object parameter style method for a better developer experience.
     */
    list(
        queries?: string[],
        total?: boolean,
        ttl?: number,
    ): Promise<Models.PresenceList>;
    list(
        paramsOrFirst?:
            { queries?: string[]; total?: boolean; ttl?: number } | string[],
        ...rest: [boolean?, number?]
    ): Promise<Models.PresenceList> {
        let params: { queries?: string[]; total?: boolean; ttl?: number };

        if (
            (typeof paramsOrFirst === 'undefined' && rest.length === 0) ||
            (paramsOrFirst &&
                typeof paramsOrFirst === 'object' &&
                !Array.isArray(paramsOrFirst))
        ) {
            params = (paramsOrFirst || {}) as {
                queries?: string[];
                total?: boolean;
                ttl?: number;
            };
        } else {
            params = {
                queries: paramsOrFirst as string[],
                total: rest[0] as boolean,
                ttl: rest[1] as number,
            };
        }

        const queries = params.queries;
        const total = params.total;
        const ttl = params.ttl;

        const apiPath = '/presences';
        const apiPayload: Payload = {};
        if (typeof queries !== 'undefined') {
            apiPayload['queries'] = queries;
        }
        if (typeof total !== 'undefined') {
            apiPayload['total'] = total;
        }
        if (typeof ttl !== 'undefined') {
            apiPayload['ttl'] = ttl;
        }
        const uri = new URL(this.client.config.endpoint + apiPath);

        const apiHeaders: { [header: string]: string } = {
            'X-Appwrite-Project': this.client.config.project,
            accept: 'application/json',
        };

        const request = this.client.call('get', uri, apiHeaders, apiPayload);
        const defaults = {
            root: 'presenceList',
            models: {
                presenceList: {
                    defaults: {
                        total: 0,
                        presences: [],
                    },
                    properties: {
                        presences: {
                            items: 'presence',
                        },
                    },
                },
                presence: {
                    defaults: {
                        $id: '',
                        $createdAt: '',
                        $updatedAt: '',
                        $permissions: [],
                        userId: '',
                        source: '',
                    },
                },
            },
        };

        return request.then((response) => applyDefaults(response, defaults));
    }

    /**
     * Get a presence log by its unique ID. Entries whose `expiresAt` is in the past are treated as not found.
     *
     *
     * @param {string} params.presenceId - Presence unique ID.
     * @throws {AppwriteException}
     * @returns {Promise<Models.Presence>}
     */
    get(params: { presenceId: string }): Promise<Models.Presence>;
    /**
     * Get a presence log by its unique ID. Entries whose `expiresAt` is in the past are treated as not found.
     *
     *
     * @param {string} presenceId - Presence unique ID.
     * @throws {AppwriteException}
     * @returns {Promise<Models.Presence>}
     * @deprecated Use the object parameter style method for a better developer experience.
     */
    get(presenceId: string): Promise<Models.Presence>;
    get(
        paramsOrFirst: { presenceId: string } | string,
    ): Promise<Models.Presence> {
        let params: { presenceId: string };

        if (
            paramsOrFirst &&
            typeof paramsOrFirst === 'object' &&
            !Array.isArray(paramsOrFirst)
        ) {
            params = (paramsOrFirst || {}) as { presenceId: string };
        } else {
            params = {
                presenceId: paramsOrFirst as string,
            };
        }

        const presenceId = params.presenceId;

        if (typeof presenceId === 'undefined' || presenceId === '') {
            throw new AppwriteException(
                'Missing required parameter: "presenceId"',
            );
        }
        const apiPath = '/presences/{presenceId}'.replace(
            '{presenceId}',
            encodeURIComponent(String(presenceId)),
        );
        const apiPayload: Payload = {};
        const uri = new URL(this.client.config.endpoint + apiPath);

        const apiHeaders: { [header: string]: string } = {
            'X-Appwrite-Project': this.client.config.project,
            accept: 'application/json',
        };

        const request = this.client.call('get', uri, apiHeaders, apiPayload);
        const defaults = {
            root: 'presence',
            models: {
                presence: {
                    defaults: {
                        $id: '',
                        $createdAt: '',
                        $updatedAt: '',
                        $permissions: [],
                        userId: '',
                        source: '',
                    },
                },
            },
        };

        return request.then((response) => applyDefaults(response, defaults));
    }

    /**
     * Create or update a presence log by its user ID.
     *
     *
     * @param {string} params.presenceId - Presence unique ID.
     * @param {string} params.status - Presence status.
     * @param {string[]} params.permissions - An array of permissions strings. By default, only the current user is granted all permissions. [Learn more about permissions](https://appwrite.io/docs/permissions).
     * @param {string} params.expiresAt - Presence expiry datetime.
     * @param {object} params.metadata - Presence metadata object.
     * @throws {AppwriteException}
     * @returns {Promise<Models.Presence>}
     */
    upsert(params: {
        presenceId: string;
        status: string;
        permissions?: string[];
        expiresAt?: string;
        metadata?: object;
    }): Promise<Models.Presence>;
    /**
     * Create or update a presence log by its user ID.
     *
     *
     * @param {string} presenceId - Presence unique ID.
     * @param {string} status - Presence status.
     * @param {string[]} permissions - An array of permissions strings. By default, only the current user is granted all permissions. [Learn more about permissions](https://appwrite.io/docs/permissions).
     * @param {string} expiresAt - Presence expiry datetime.
     * @param {object} metadata - Presence metadata object.
     * @throws {AppwriteException}
     * @returns {Promise<Models.Presence>}
     * @deprecated Use the object parameter style method for a better developer experience.
     */
    upsert(
        presenceId: string,
        status: string,
        permissions?: string[],
        expiresAt?: string,
        metadata?: object,
    ): Promise<Models.Presence>;
    upsert(
        paramsOrFirst:
            | {
                  presenceId: string;
                  status: string;
                  permissions?: string[];
                  expiresAt?: string;
                  metadata?: object;
              }
            | string,
        ...rest: [string?, string[]?, string?, object?]
    ): Promise<Models.Presence> {
        let params: {
            presenceId: string;
            status: string;
            permissions?: string[];
            expiresAt?: string;
            metadata?: object;
        };

        if (
            paramsOrFirst &&
            typeof paramsOrFirst === 'object' &&
            !Array.isArray(paramsOrFirst)
        ) {
            params = (paramsOrFirst || {}) as {
                presenceId: string;
                status: string;
                permissions?: string[];
                expiresAt?: string;
                metadata?: object;
            };
        } else {
            params = {
                presenceId: paramsOrFirst as string,
                status: rest[0] as string,
                permissions: rest[1] as string[],
                expiresAt: rest[2] as string,
                metadata: rest[3] as object,
            };
        }

        const presenceId = params.presenceId;
        const status = params.status;
        const permissions = params.permissions;
        const expiresAt = params.expiresAt;
        const metadata = params.metadata;

        if (typeof presenceId === 'undefined' || presenceId === '') {
            throw new AppwriteException(
                'Missing required parameter: "presenceId"',
            );
        }
        if (typeof status === 'undefined') {
            throw new AppwriteException('Missing required parameter: "status"');
        }
        const apiPath = '/presences/{presenceId}'.replace(
            '{presenceId}',
            encodeURIComponent(String(presenceId)),
        );
        const apiPayload: Payload = {};
        if (typeof status !== 'undefined') {
            apiPayload['status'] = status;
        }
        if (typeof permissions !== 'undefined') {
            apiPayload['permissions'] = permissions;
        }
        if (typeof expiresAt !== 'undefined') {
            apiPayload['expiresAt'] = expiresAt;
        }
        if (typeof metadata !== 'undefined') {
            apiPayload['metadata'] = metadata;
        }
        const uri = new URL(this.client.config.endpoint + apiPath);

        const apiHeaders: { [header: string]: string } = {
            'X-Appwrite-Project': this.client.config.project,
            'content-type': 'application/json',
            accept: 'application/json',
        };

        const request = this.client.call('put', uri, apiHeaders, apiPayload);
        const defaults = {
            root: 'presence',
            models: {
                presence: {
                    defaults: {
                        $id: '',
                        $createdAt: '',
                        $updatedAt: '',
                        $permissions: [],
                        userId: '',
                        source: '',
                    },
                },
            },
        };

        return request.then((response) => applyDefaults(response, defaults));
    }

    /**
     * Update a presence log by its unique ID. Using the patch method you can pass only specific fields that will get updated.
     *
     *
     * @param {string} params.presenceId - Presence unique ID.
     * @param {string} params.status - Presence status.
     * @param {string} params.expiresAt - Presence expiry datetime.
     * @param {object} params.metadata - Presence metadata object.
     * @param {string[]} params.permissions - An array of permissions strings. By default, only the current user is granted all permissions. [Learn more about permissions](https://appwrite.io/docs/permissions).
     * @param {boolean} params.purge - When true, purge cached responses used by list presences endpoint.
     * @throws {AppwriteException}
     * @returns {Promise<Models.Presence>}
     */
    update(params: {
        presenceId: string;
        status?: string;
        expiresAt?: string;
        metadata?: object;
        permissions?: string[];
        purge?: boolean;
    }): Promise<Models.Presence>;
    /**
     * Update a presence log by its unique ID. Using the patch method you can pass only specific fields that will get updated.
     *
     *
     * @param {string} presenceId - Presence unique ID.
     * @param {string} status - Presence status.
     * @param {string} expiresAt - Presence expiry datetime.
     * @param {object} metadata - Presence metadata object.
     * @param {string[]} permissions - An array of permissions strings. By default, only the current user is granted all permissions. [Learn more about permissions](https://appwrite.io/docs/permissions).
     * @param {boolean} purge - When true, purge cached responses used by list presences endpoint.
     * @throws {AppwriteException}
     * @returns {Promise<Models.Presence>}
     * @deprecated Use the object parameter style method for a better developer experience.
     */
    update(
        presenceId: string,
        status?: string,
        expiresAt?: string,
        metadata?: object,
        permissions?: string[],
        purge?: boolean,
    ): Promise<Models.Presence>;
    update(
        paramsOrFirst:
            | {
                  presenceId: string;
                  status?: string;
                  expiresAt?: string;
                  metadata?: object;
                  permissions?: string[];
                  purge?: boolean;
              }
            | string,
        ...rest: [string?, string?, object?, string[]?, boolean?]
    ): Promise<Models.Presence> {
        let params: {
            presenceId: string;
            status?: string;
            expiresAt?: string;
            metadata?: object;
            permissions?: string[];
            purge?: boolean;
        };

        if (
            paramsOrFirst &&
            typeof paramsOrFirst === 'object' &&
            !Array.isArray(paramsOrFirst)
        ) {
            params = (paramsOrFirst || {}) as {
                presenceId: string;
                status?: string;
                expiresAt?: string;
                metadata?: object;
                permissions?: string[];
                purge?: boolean;
            };
        } else {
            params = {
                presenceId: paramsOrFirst as string,
                status: rest[0] as string,
                expiresAt: rest[1] as string,
                metadata: rest[2] as object,
                permissions: rest[3] as string[],
                purge: rest[4] as boolean,
            };
        }

        const presenceId = params.presenceId;
        const status = params.status;
        const expiresAt = params.expiresAt;
        const metadata = params.metadata;
        const permissions = params.permissions;
        const purge = params.purge;

        if (typeof presenceId === 'undefined' || presenceId === '') {
            throw new AppwriteException(
                'Missing required parameter: "presenceId"',
            );
        }
        const apiPath = '/presences/{presenceId}'.replace(
            '{presenceId}',
            encodeURIComponent(String(presenceId)),
        );
        const apiPayload: Payload = {};
        if (typeof status !== 'undefined') {
            apiPayload['status'] = status;
        }
        if (typeof expiresAt !== 'undefined') {
            apiPayload['expiresAt'] = expiresAt;
        }
        if (typeof metadata !== 'undefined') {
            apiPayload['metadata'] = metadata;
        }
        if (typeof permissions !== 'undefined') {
            apiPayload['permissions'] = permissions;
        }
        if (typeof purge !== 'undefined') {
            apiPayload['purge'] = purge;
        }
        const uri = new URL(this.client.config.endpoint + apiPath);

        const apiHeaders: { [header: string]: string } = {
            'X-Appwrite-Project': this.client.config.project,
            'content-type': 'application/json',
            accept: 'application/json',
        };

        const request = this.client.call('patch', uri, apiHeaders, apiPayload);
        const defaults = {
            root: 'presence',
            models: {
                presence: {
                    defaults: {
                        $id: '',
                        $createdAt: '',
                        $updatedAt: '',
                        $permissions: [],
                        userId: '',
                        source: '',
                    },
                },
            },
        };

        return request.then((response) => applyDefaults(response, defaults));
    }

    /**
     * Delete a presence log by its unique ID.
     *
     *
     * @param {string} params.presenceId - Presence unique ID.
     * @throws {AppwriteException}
     * @returns {Promise<{}>}
     */
    delete(params: { presenceId: string }): Promise<{}>;
    /**
     * Delete a presence log by its unique ID.
     *
     *
     * @param {string} presenceId - Presence unique ID.
     * @throws {AppwriteException}
     * @returns {Promise<{}>}
     * @deprecated Use the object parameter style method for a better developer experience.
     */
    delete(presenceId: string): Promise<{}>;
    delete(paramsOrFirst: { presenceId: string } | string): Promise<{}> {
        let params: { presenceId: string };

        if (
            paramsOrFirst &&
            typeof paramsOrFirst === 'object' &&
            !Array.isArray(paramsOrFirst)
        ) {
            params = (paramsOrFirst || {}) as { presenceId: string };
        } else {
            params = {
                presenceId: paramsOrFirst as string,
            };
        }

        const presenceId = params.presenceId;

        if (typeof presenceId === 'undefined' || presenceId === '') {
            throw new AppwriteException(
                'Missing required parameter: "presenceId"',
            );
        }
        const apiPath = '/presences/{presenceId}'.replace(
            '{presenceId}',
            encodeURIComponent(String(presenceId)),
        );
        const apiPayload: Payload = {};
        const uri = new URL(this.client.config.endpoint + apiPath);

        const apiHeaders: { [header: string]: string } = {
            'X-Appwrite-Project': this.client.config.project,
            'content-type': 'application/json',
            accept: 'application/json',
        };

        return this.client.call('delete', uri, apiHeaders, apiPayload);
    }
}
